import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Search, History, User, Mail, MessageSquare, Calendar, Eye, X, AlertCircle } from 'lucide-react';
import { BASE_URL } from '../config';

export default function DoctorsHistory() {
  const [doctors, setDoctors] = useState([]);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDoctorsHistory();
  }, []);

  useEffect(() => {
    filterHistory();
  }, [selectedDoctor, searchTerm, history]);

  const fetchDoctorsHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${BASE_URL}/api/hospitals/doctors-history/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDoctors(res.data.doctors);
      setHistory(res.data.history);
      setFilteredHistory(res.data.history);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterHistory = () => {
    let filtered = history;

    // Filter by selected doctor
    if (selectedDoctor) {
      filtered = filtered.filter(h => h.doctor_email === selectedDoctor);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(h =>
        h.user_query.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.ai_response.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.doctor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredHistory(filtered);
  };

  const handleViewChat = (chat) => {
    setSelectedChat(chat);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-medical-500 to-medical-600 rounded-xl shadow-sm p-6 text-white"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
            <History className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Doctors Chat History</h2>
            <p className="text-medical-100 text-sm">View chat history of doctors registered under your hospital</p>
          </div>
        </div>
      </motion.div>

      {/* Doctors Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div
          onClick={() => setSelectedDoctor(null)}
          className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all ${
            !selectedDoctor ? 'ring-2 ring-medical-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">All Doctors</p>
              <p className="text-2xl font-bold text-slate-900">{doctors.length}</p>
            </div>
            <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-primary-400" />
            </div>
          </div>
        </div>

        {doctors.slice(0, 3).map((doctor, index) => (
          <motion.div
            key={doctor.user_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            onClick={() => setSelectedDoctor(doctor.email)}
            className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all ${
              selectedDoctor === doctor.email ? 'ring-2 ring-medical-500' : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-primary-400" />
              </div>
              <span className="text-xs bg-slate-750 text-slate-600 px-2 py-1 rounded-full">
                {doctor.chat_count} chats
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-900 truncate">{doctor.name}</p>
            <p className="text-xs text-slate-600 truncate">{doctor.email}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm p-6"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
            <input
              type="text"
              placeholder="Search chat history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          <select
            value={selectedDoctor || ''}
            onChange={(e) => setSelectedDoctor(e.target.value || null)}
            className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
          >
            <option value="">All Doctors</option>
            {doctors.map(doctor => (
              <option key={doctor.user_id} value={doctor.email}>
                {doctor.name}
              </option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Chat History List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm overflow-hidden"
      >
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            Chat History ({filteredHistory.length})
          </h3>
        </div>

        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-medical-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading chat history...</p>
            </div>
          ) : filteredHistory.length > 0 ? (
            filteredHistory.map((chat, index) => (
              <motion.div
                key={chat.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="p-4 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-primary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{chat.doctor_name}</p>
                        <p className="text-xs text-slate-600">{chat.doctor_email}</p>
                      </div>
                      {chat.severity_level && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          chat.severity_level === 'high' ? 'bg-red-100 text-red-700' :
                          chat.severity_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {chat.severity_level}
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-sm text-slate-700 font-medium mb-1">Query:</p>
                      <p className="text-sm text-slate-600 line-clamp-2">{chat.user_query}</p>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(chat.timestamp)}
                      </span>
                      {chat.diagnosis_type && (
                        <span className="px-2 py-0.5 bg-slate-750 rounded">
                          {chat.diagnosis_type}
                        </span>
                      )}
                      {chat.chat_title && (
                        <span className="truncate max-w-xs">
                          {chat.chat_title}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleViewChat(chat)}
                    className="px-3 py-2 text-sm text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No chat history found</p>
              <p className="text-slate-600 text-sm mt-2">
                {selectedDoctor ? 'This doctor has no chat history yet' : 'No doctors have started chatting yet'}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Chat Modal */}
      <AnimatePresence>
        {selectedChat && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedChat(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-medical-500 to-medical-600 text-white p-6 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Chat Details</h3>
                      <p className="text-medical-100 text-sm">{selectedChat.doctor_name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedChat(null)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Doctor Info */}
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary-400" />
                    Doctor Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Name</p>
                      <p className="text-base font-medium text-slate-900">{selectedChat.doctor_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Email</p>
                      <p className="text-base font-medium text-slate-900">{selectedChat.doctor_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Timestamp</p>
                      <p className="text-base font-medium text-slate-900">{formatDate(selectedChat.timestamp)}</p>
                    </div>
                    {selectedChat.severity_level && (
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Severity</p>
                        <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${
                          selectedChat.severity_level === 'high' ? 'bg-red-100 text-red-700' :
                          selectedChat.severity_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {selectedChat.severity_level}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* User Query */}
                <div className="bg-primary-500/10 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary-400" />
                    User Query
                  </h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedChat.user_query}</p>
                </div>

                {/* AI Response */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-green-600" />
                    AI Response
                  </h4>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedChat.ai_response}</p>
                </div>

                {/* Additional Info */}
                {(selectedChat.source || selectedChat.trigger || selectedChat.diagnosis_type) && (
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-3">Additional Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedChat.source && (
                        <div>
                          <p className="text-sm text-slate-600 mb-1">Source</p>
                          <p className="text-base font-medium text-slate-900">{selectedChat.source}</p>
                        </div>
                      )}
                      {selectedChat.trigger && (
                        <div>
                          <p className="text-sm text-slate-600 mb-1">Trigger</p>
                          <p className="text-base font-medium text-slate-900">{selectedChat.trigger}</p>
                        </div>
                      )}
                      {selectedChat.diagnosis_type && (
                        <div>
                          <p className="text-sm text-slate-600 mb-1">Diagnosis Type</p>
                          <p className="text-base font-medium text-slate-900">{selectedChat.diagnosis_type}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
